import React, { useEffect, useState } from 'react'
import { Box, Alert, CircularProgress, Tabs, Tab } from '@mui/material'
import { apiService } from '../services/api'
import {
  ReportPage, HeaderButton, FilterBar, KpiStrip, Section, ReportTable, Icon, fmt,
  useDistinctFilters, buildPlkFilterFields,
} from '../components/report-shell'

// Region / RKS — podsumowanie MTD (Plus).
//
// Mirrors "Raport Plus do Pawła.xlsx": Arkusz1 (region rollup) -> tab
// "Regiony", Arkusz2 (per-RKS breakdown per region) -> tab "RKS per region",
// Arkusz3 (SFID -> KAM mapping) -> tab "Salony ↔ KAM".
//
// "Zrealizowane" / "W trakcie" are intentionally not shown — confirmed with
// the PO (call 2026-08-04) that the plain MTD total is sufficient, see
// backend/app/routers/plus_region_reports.py docstring.

const pctFmt = (v) => fmt.pct((Number(v) || 0) * 100)

// Business target from the PO (call 2026-08-04): Opaska SOS share should be
// at least 45% of the Watch Sigma + Opaska SOS mix. Badge thresholds are on
// the same 0–1 fraction scale as `pct_opaska_sos` itself.
const OPASKA_SOS_TARGET = 0.45
const OPASKA_SOS_WARN = 0.30

const regionCols = [
  { field: 'region', header: 'Region', width: '1.6fr', minWidth: 180 },
  { field: 'salon_count', header: 'Salonów (aktywne)', width: '140px', numeric: true },
  { field: 'total_sold', header: 'TOTAL (MTD)', width: '110px', sales: true },
  { field: 'salons_with_sale', header: 'Salony ze sprzedażą', width: '150px', numeric: true },
  { field: 'salons_zero', header: 'Salony bez sprzedaży', width: '145px', numeric: true },
  { field: 'pct_zero', header: 'Udział % zerówek', width: '135px', numeric: true, format: pctFmt },
  { field: 'avg_realization_per_pos', header: 'Śr. realizacja / POS', width: '150px',
    numeric: true, format: (v) => (Number(v) || 0).toFixed(2) },
  { field: 'watch_sigma', header: 'Watch Sigma', width: '110px', numeric: true },
  { field: 'opaska_sos', header: 'Opaska SOS', width: '110px', numeric: true },
  { field: 'pct_opaska_sos', header: 'Udział % Opaska SOS', width: '150px',
    percent: { thresholds: [OPASKA_SOS_WARN, OPASKA_SOS_TARGET] }, format: pctFmt },
]

const rksCols = regionCols.map((c) => (c.field === 'region' ? { ...c, field: 'rks', header: 'RKS' } : c))

const kamMappingCols = [
  { field: 'sfid', header: 'SFID', width: '100px', numeric: true },
  { field: 'agent', header: 'Agent', width: '1.6fr', minWidth: 200 },
  { field: 'miasto', header: 'Miasto', width: '140px' },
  { field: 'region', header: 'Region', width: '170px' },
  { field: 'rks', header: 'RKS', width: '170px' },
  { field: 'kam', header: 'KAM (LOCON)', width: '160px' },
  { field: 'method', header: 'Metoda', width: '130px',
    format: (v) => ({ canonical: 'przypisanie KAM', legacy_kam_code: 'kam_code (pos_data)', brak: 'brak' }[v] || v) },
]

const kamSummaryCols = [
  { field: 'kam', header: 'KAM', width: '1.6fr', minWidth: 180 },
  { field: 'salon_count', header: 'Liczba salonów', width: '140px', numeric: true },
  { field: 'share', header: 'Udział', width: '110px', numeric: true, format: pctFmt },
]

const RegionAverageLine = ({ avg }) => {
  if (!avg) return null
  return (
    <Box sx={{
      display: 'flex', gap: 3, flexWrap: 'wrap', px: 2, py: 1, mb: 1,
      fontSize: 13, color: '#475569', background: '#f8fafc', borderRadius: 1,
    }}>
      <span><b>ŚREDNIA REGIONU</b></span>
      <span>Salonów: <b>{avg.salon_count}</b></span>
      <span>TOTAL (MTD): <b>{avg.total_sold}</b></span>
      <span>Bez sprzedaży: <b>{avg.salons_zero}</b> ({pctFmt(avg.pct_zero)})</span>
      <span>Śr. realizacja/POS: <b>{(avg.avg_realization_per_pos || 0).toFixed(2)}</b></span>
      <span>Opaska SOS: <b>{avg.opaska_sos}</b> (
        <b style={{ color: avg.pct_opaska_sos >= OPASKA_SOS_TARGET ? '#16a34a' : '#dc2626' }}>
          {pctFmt(avg.pct_opaska_sos)}
        </b>, cel ≥{OPASKA_SOS_TARGET * 100}%)</span>
    </Box>
  )
}

// Mirrors "TABELA 7 — PODSUMOWANIE" at the bottom of her Arkusz2: Sieć
// stacjonarna + Kanały zdalne (sales with no assigned salon — SFID
// 30000235/60000002/60000010) feeding into POLSKA — RAZEM. Only present
// when no filter is active (remote-channel sales can't be scoped by
// region/agent/RKS/etc. since they have no salon).
const PolandTotalLine = ({ t }) => {
  if (!t) return null
  const remote = t.remote_channels
  const stationary = t.stationary
  return (
    <Box sx={{ mt: 1 }}>
      {remote && stationary && (
        <Box sx={{
          display: 'flex', gap: 3, flexWrap: 'wrap', px: 2, py: 0.75,
          fontSize: 12.5, color: '#475569', background: '#f8fafc',
        }}>
          <span style={{ minWidth: 150 }}>Sieć stacjonarna</span>
          <span>TOTAL: {stationary.total_sold}</span>
          <span>Watch Sigma: {stationary.watch_sigma}</span>
          <span>Opaska SOS: {stationary.opaska_sos}</span>
        </Box>
      )}
      {remote && stationary && (
        <Box sx={{
          display: 'flex', gap: 3, flexWrap: 'wrap', px: 2, py: 0.75,
          fontSize: 12.5, color: '#475569', background: '#f8fafc', fontStyle: 'italic',
        }}>
          <span style={{ minWidth: 150, fontStyle: 'italic' }}>Kanały zdalne (bez RKS)</span>
          <span>TOTAL: {remote.total_sold}</span>
          <span>Watch Sigma: {remote.watch_sigma}</span>
          <span>Opaska SOS: {remote.opaska_sos}</span>
        </Box>
      )}
      <Box sx={{
        display: 'flex', gap: 3, flexWrap: 'wrap', px: 2, py: 1.25,
        fontSize: 13, color: '#14532d', background: '#f0fdf4',
        borderTop: '2px solid #16a34a', borderRadius: '0 0 4px 4px',
        fontWeight: 700,
      }}>
        <span>POLSKA — RAZEM</span>
        <span>Salonów: {t.salon_count}</span>
        <span>TOTAL (MTD): {t.total_sold}</span>
        <span>Bez sprzedaży: {t.salons_zero} ({pctFmt(t.pct_zero)})</span>
        <span>Śr. realizacja/POS: {(t.avg_realization_per_pos || 0).toFixed(2)}</span>
        <span>Watch Sigma: {t.watch_sigma}</span>
        <span>Opaska SOS: {t.opaska_sos} (
          <span style={{ color: (t.pct_opaska_sos || 0) >= OPASKA_SOS_TARGET ? '#16a34a' : '#dc2626' }}>
            {pctFmt(t.pct_opaska_sos)}
          </span>)</span>
      </Box>
    </Box>
  )
}

const PlusRegionRollupPage = () => {
  const [filters, setFilters] = useState({})
  const [data, setData] = useState(null)
  const [mapping, setMapping] = useState(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState(null)
  const [tab, setTab] = useState(0)

  const distinct = useDistinctFilters('plk')

  const fetchData = async () => {
    setLoading(true); setError(null)
    try {
      const [rollupResp, mappingResp] = await Promise.all([
        apiService.getPlusRegionRollup(filters),
        apiService.getPlusSalonKamMapping(filters),
      ])
      setData(rollupResp.data)
      setMapping(mappingResp.data)
    } catch (err) {
      // Drop the previous result set: otherwise a failed refetch leaves the
      // old filter's numbers on screen underneath the new filter chips.
      setData(null); setMapping(null)
      setError(err?.response?.data?.detail || err.message)
    } finally { setLoading(false) }
  }

  const exportExcel = async () => {
    setExporting(true)
    try {
      // Single file, every tab (Regiony/RKS/Salony/KAM) — one click, one
      // download. Previously this fired two separate exports silently;
      // whichever file you opened first looked incomplete because the
      // other tabs' data was in the other one.
      const resp = await apiService.exportPlusRegionRollupAll(filters)
      const url = window.URL.createObjectURL(new Blob([resp.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `Plus_region_rks_kam_${data?.month_start || ''}.xlsx`)
      document.body.appendChild(link); link.click(); link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
    } finally { setExporting(false) }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData() }, [JSON.stringify(filters)])

  const T = data?.totals || {}

  const filterBar = (
    <FilterBar
      periodRight={data?.month_start ? `MTD od ${data.month_start}` : null}
      filters={buildPlkFilterFields(distinct, filters, setFilters)}
      onReset={() => setFilters({})}
    />
  )

  const kpiTiles = data ? [
    { key: 'salons', label: 'Salony aktywne', value: fmt.int(T.salon_count || 0) },
    { key: 'mtd', label: 'Sprzedaż MTD', value: fmt.int(T.total_sold || 0), unit: 'szt.', accent: '#00a651' },
    { key: 'zero', label: 'Salony bez sprzedaży',
      value: `${T.salons_zero || 0} / ${T.salon_count || 0}`, accent: '#dc2626',
      sub: pctFmt(T.pct_zero || 0) },
    { key: 'real', label: 'Śr. realizacja / POS', value: (T.avg_realization_per_pos || 0).toFixed(2), unit: 'szt.', accent: '#2563eb' },
    { key: 'sos', label: 'Udział Opaska SOS', value: pctFmt(T.pct_opaska_sos || 0),
      accent: (T.pct_opaska_sos || 0) >= OPASKA_SOS_TARGET ? '#16a34a' : '#dc2626',
      sub: `${fmt.int(T.opaska_sos || 0)} Opaska / ${fmt.int(T.watch_sigma || 0)} Watch Sigma · cel ≥${OPASKA_SOS_TARGET * 100}%` },
  ] : []

  return (
    <ReportPage
      title="Plus — Region / RKS (podsumowanie)"
      subtitle="Rollup sprzedaży MTD per region i RKS, salony bez sprzedaży, śr. realizacja/POS, Watch Sigma / Opaska SOS + mapa salon↔KAM."
      breadcrumb={['KAM-SFA', 'Plus', 'Region / RKS']}
      actions={
        <>
          <HeaderButton onClick={fetchData} disabled={loading}>
            <Icon.refresh /> {loading ? 'Ładowanie…' : 'Odśwież'}
          </HeaderButton>
          <HeaderButton primary onClick={exportExcel} disabled={exporting || !data}>
            <Icon.download /> {exporting ? 'Eksport…' : 'Eksport Excel'}
          </HeaderButton>
        </>
      }
      filterBar={filterBar}
      kpiStrip={kpiTiles.length > 0 ? <KpiStrip tiles={kpiTiles} /> : null}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}

      {data && !loading && (
        <>
          <Box sx={{ mb: 2 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              <Tab label={`Regiony (${data.regions.length})`} />
              <Tab label="RKS per region" />
              <Tab label={`Salony ↔ KAM (${mapping?.salons?.length || 0})`} />
            </Tabs>
          </Box>

          {tab === 0 && (
            <Section title="Rollup per region">
              <ReportTable
                rows={data.regions.map((r) => ({ id: r.region, ...r }))}
                columns={regionCols}
                initialSort={{ field: 'total_sold', dir: 'desc' }}
                pageSize={0}
                density="compact"
              />
              <PolandTotalLine t={T} />
            </Section>
          )}

          {tab === 1 && data.rks_by_region.map((grp) => (
            <Section key={grp.region} title={grp.region} padded>
              <RegionAverageLine avg={grp.average} />
              <ReportTable
                rows={grp.rks.map((r) => ({ id: r.rks, ...r }))}
                columns={rksCols}
                initialSort={{ field: 'total_sold', dir: 'desc' }}
                pageSize={0}
                density="compact"
              />
            </Section>
          ))}

          {tab === 2 && mapping && (
            <>
              <Section title="Salony (SFID) ↔ KAM (LOCON)">
                <ReportTable
                  rows={mapping.salons.map((s, i) => ({ id: s.sfid || i, ...s }))}
                  columns={kamMappingCols}
                  initialSort={{ field: 'region', dir: 'asc' }}
                  pageSize={50}
                  pageSizeOptions={[25, 50, 100, 500]}
                  density="compact"
                />
              </Section>
              <Section title="Salonów per KAM">
                <ReportTable
                  rows={mapping.kam_summary.map((r) => ({ id: r.kam, ...r }))}
                  columns={kamSummaryCols}
                  initialSort={{ field: 'salon_count', dir: 'desc' }}
                  pageSize={0}
                  density="compact"
                />
              </Section>
            </>
          )}
        </>
      )}
    </ReportPage>
  )
}

export default PlusRegionRollupPage
